import { Component, Inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';
import { FinancialClientStatementReportDTO } from '@gipi-financial/client/models/dto/client-statement-report.dto';
import { FinancialClientService } from '@gipi-financial/client/services/client.service';

import { FinancialSaleFilterDTO } from '@gipi-financial/sale/models/dto/sale-filter.dto';

import { RegistrationClient } from '@gipi-registration/client/models/client.model';
import { RegistrationClientCardDTO } from '@gipi-registration/client/models/dto/client-card.dto';
import { FinancialClientSaleDTO } from '@gipi-registration/client/models/dto/client-sale.dto';
import { PdfClientFileExtractService } from '@gipi-registration/client/services/pdf-client-file-extract.service';
import { RegistrationPhone } from '@gipi-registration/person/models/phone.model';
import { SaleDataDialogComponent } from '@gipi-sale/sale/components/sale-data-dialog/sale-data-dialog.component';
import { WhatsappContactDialogComponent } from '@gipi-shared/components/whatsapp-contact-dialog/whatsapp-contact-dialog.component';
import { WhatsappContact } from '@gipi-shared/models/whatsapp-contact.model';
import { CustomMessageService } from '@gipi-shared/services/custom-message.service';
import { AbstractComponent, ArrayUtil, CurrencyUtil, DateUtil, DialogService, GIPIPageModel, ObjectUtil, PageDTO, StringUtil, TableColumnBuilder, TableColumnDTO } from '@gipisistemas/ng-core';
import { Observable, of } from 'rxjs';

@Component({
    templateUrl: './client-dialog.component.html',
    styleUrls: ['./client-dialog.component.scss'],
})
export class ClientDialogComponent extends AbstractComponent implements OnInit {

    public client: RegistrationClientCardDTO;

    public pageSale: PageDTO<FinancialClientSaleDTO>;
    public clientStatementList: FinancialClientStatementReportDTO[];

    public columnsSale: TableColumnDTO[] = [];
    public columnsClientStatement: TableColumnDTO[] = [];

    @ViewChild('actions', { static: true }) actions: TemplateRef<any>;

    public get totalAmountReceivable(): Observable<string> {
        if (ObjectUtil.isNull(this.client)) {
            return of(CurrencyUtil.transform(0, '1.2-2'));
        }

        const total: number = this.client.amountReceivableInOverdue +
            this.client.amountReceivableToday +
            this.client.amountReceivable +
            this.client.totalInterest +
            this.client.totalFine;

        return of(CurrencyUtil.transform(total, '1.2-2'));
    }

    /** Essa formatação é a exibida no tooltip. Portanto evitar alterá-la. */
    public get tooltipAverageDelay(): string { // (Pendentes)
        return (`Definição:
Calcula a média de dias de atraso apenas das parcelas vencidas e não pagas (ou seja, inadimplência atual).

Uso comum:
Análise de risco presente, cobrança ativa, foco em quem ainda está devendo.
        `).trim();
    }

    /** Essa formatação é a exibida no tooltip. Portanto evitar alterá-la. */
    public get tooltipAverageDelayLateInstallments(): string {
        return (`Definição:
Calcula a média de atraso considerando todas as parcelas vencidas (pagas ou não), desde o início do histórico.

Uso comum:
Avaliação de comportamento passado do pagador, tendências de inadimplência.
        `).trim();
    }

    constructor(
        protected messageService: CustomMessageService,
        protected router: Router,
        protected activatedRoute: ActivatedRoute,
        private _clientService: FinancialClientService,
        private _pdfClientFileExtractService: PdfClientFileExtractService,
        private _dialogService: DialogService,
        @Inject(MAT_DIALOG_DATA) public data: RegistrationClientCardDTO,
    ) {
        super(messageService, router, activatedRoute);
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.createTableColumnsSale();
        this.createTableColumnsClientStatement();
        this.pageSale = new GIPIPageModel<FinancialClientSaleDTO>();
        this.client = this.data;
    }

    protected createTableColumnsSale(): void {
        this.columnsSale = [
            TableColumnBuilder.instance()
                .property('date')
                .name('Data')
                .value((obj: FinancialClientSaleDTO) => DateUtil.format(obj.date, DateUtil.DATE_FORMAT))
                .align('center center')
                .width(12)
                .build(),
            TableColumnBuilder.instance()
                .property('documentNumber')
                .name('Documento')
                .value((obj: FinancialClientSaleDTO) => !StringUtil.isEmpty(obj.documentNumber) ? obj.documentNumber : '')
                .align('center center')
                .width(12)
                .build(),
            TableColumnBuilder.instance()
                .property('chargeType')
                .name('Forma de pagamento')
                .value((obj: FinancialClientSaleDTO) => !StringUtil.isEmpty(obj.paymentMethodName) ? obj.paymentMethodName : '')
                .build(),
            TableColumnBuilder.instance()
                .property('seller')
                .name('Vendedor')
                .value((obj: FinancialClientSaleDTO) => !StringUtil.isEmpty(obj.sellerName) ? obj.sellerName : '')
                .build(),
            TableColumnBuilder.instance()
                .property('grossAmount')
                .name('Total bruto')
                .value((obj: FinancialClientSaleDTO) => CurrencyUtil.transform(obj.grossAmount, '1.2-2'))
                .align('center center')
                .width(12)
                .build(),
            TableColumnBuilder.instance()
                .property('amountNet')
                .name('Total Líquido')
                .value((obj: FinancialClientSaleDTO) => CurrencyUtil.transform(obj.amountNet, '1.2-2'))
                .align('center center')
                .width(12)
                .build(),
            TableColumnBuilder.instance()
                .property('actions')
                .name('Ações')
                .width(7)
                .align('center center')
                .template(this.actions)
                .build(),
        ];
    }

    protected createTableColumnsClientStatement(): void {
        this.columnsClientStatement = [
            TableColumnBuilder.instance()
                .property('documentNumber')
                .name('N° Documento')
                .value((obj: FinancialClientStatementReportDTO) => !StringUtil.isEmpty(obj.documentNumber) ? obj.documentNumber : '')
                .align('center center')
                .build(),
            TableColumnBuilder.instance()
                .property('dueDate')
                .name('Vencimento')
                .value((obj: FinancialClientStatementReportDTO) => DateUtil.format(obj.dueDate, DateUtil.DATE_FORMAT))
                .align('center center')
                .build(),
            TableColumnBuilder.instance()
                .property('expiredDays')
                .name('Dias')
                .value((obj: FinancialClientStatementReportDTO) => obj.expiredDays ? obj.expiredDays : '')
                .align('center center')
                .build(),
            TableColumnBuilder.instance()
                .property('amount')
                .name('Valor')
                .value((obj: FinancialClientStatementReportDTO) => CurrencyUtil.transform(obj.amount, '1.2-2'))
                .align('center center')
                .build(),
            TableColumnBuilder.instance()
                .property('interest')
                .name('Juros')
                .value((obj: FinancialClientStatementReportDTO) => CurrencyUtil.transform(obj.interest, '1.2-2'))
                .align('center center')
                .build(),
            TableColumnBuilder.instance()
                .property('fine')
                .name('Multas')
                .value((obj: FinancialClientStatementReportDTO) => CurrencyUtil.transform(obj.fine, '1.2-2'))
                .align('center center')
                .build(),
            TableColumnBuilder.instance()
                .property('amountNet')
                .name('Total')
                .value((obj: FinancialClientStatementReportDTO) => CurrencyUtil.transform(obj.amountNet, '1.2-2'))
                .align('center center')
                .build(),
        ];
    }

    public findSale(pageEvent?: PageEvent): void {
        try {
            this.loading = true;
            this.pageSale = new PageDTO<FinancialClientSaleDTO>();

            const saleFilter: FinancialSaleFilterDTO = new FinancialSaleFilterDTO();
            saleFilter.client = new RegistrationClient();
            saleFilter.client.id = this.client.id;
            saleFilter.pageSize = 10;

            if (pageEvent) {
                saleFilter.pageNumber = pageEvent.pageIndex;
                saleFilter.offset = pageEvent.pageIndex * pageEvent.pageSize;
            } else {
                saleFilter.pageNumber = 0;
                saleFilter.offset = 0;
            }

            this._clientService.clientSale(saleFilter.client.id, saleFilter.pageNumber, saleFilter.pageSize).toPromise().then(page => {
                this.pageSale = page;
                this.loading = false;
            }).catch(error => {
                this.loading = false;
                this.addErrorMessage(error);
            });
        } catch (e) {
            this.loading = false;
            this.addErrorMessage(e);
        }
    }

    public findCardExtract(): void {
        try {
            if (!ArrayUtil.isEmpty(this.clientStatementList)) {
                return;
            }

            this.loading = true;

            this._clientService.getCardExtractBill(this.client.id).toPromise().then(list => {
                this.clientStatementList = list;
                this.loading = false;
            }).catch(error => {
                this.loading = false;
                this.addErrorMessage(error);
            });
        } catch (e) {
            this.loading = false;
            this.addErrorMessage(e);
        }
    }

    public exportClientStatement(): void {
        try {
            this.loading = true;

            const cpfOrCnpj: string = !StringUtil.isEmpty(this.client.cnpj)
                ? StringUtil.format(this.client.cnpj, '00.000.000/0000-00')
                : StringUtil.format(this.client.cpf, '000.000.000-00');

            this._pdfClientFileExtractService.exportPdf({
                clientName: this.client.name,
                clientCpfOrCnpj: cpfOrCnpj,
                clientStatementList: this.clientStatementList,
            });
            this.loading = false;
        } catch (e) {
            this.loading = false;
            this.handleError(e);
        }
    }

    public getColorByAmount(amount: number): string {
        if (amount > 0) {
            return '#47ca82';
        } else if (amount < 0) {
            return '#ca0045';
        } else if (amount === 0) {
            return '#696969';
        }
    }

    public navigateSaleData(entity: FinancialClientSaleDTO): void {
        this._dialogService.open({
            componentOrTemplateRef: SaleDataDialogComponent,
            data: entity.saleSaleId,
            width: '90%',
            height: '90%',
        });
    }

    public async whatsappContact(): Promise<void> {
        try {
            if (!ObjectUtil.isNull(this.client)) {
                const getRecipientPhone = (phoneList: RegistrationPhone[]) => {
                    if (ArrayUtil.isEmpty(phoneList)) {
                        return '';
                    }

                    const phone: RegistrationPhone = phoneList.find(e => e.type.type === 'WHATSAPP');
                    return !ObjectUtil.isNull(phone) ? phone.number : '';
                };

                const recipient: RegistrationClient = await this._clientService.getOne(this.client.id).toPromise();
                const recipientPhoneAux: string = getRecipientPhone(recipient.person.phoneList);

                const keyLocalStorage: string = '@GIPIClientCardMessage';
                const savedMessage: string = WhatsappContactDialogComponent.getMessage(keyLocalStorage);

                const whatsappContact: WhatsappContact = new WhatsappContact();
                whatsappContact.recipientName = this.client.name;
                whatsappContact.recipientPhone = recipientPhoneAux;
                whatsappContact.message = !StringUtil.isEmpty(savedMessage) ? savedMessage : '';

                this._dialogService.open({
                    componentOrTemplateRef: WhatsappContactDialogComponent,
                    data: whatsappContact,
                    width: '55rem',
                    maxHeight: '55rem',
                }).afterClosed().toPromise().then((message: string) => {
                    if (!StringUtil.isEmpty(message)) {
                        WhatsappContactDialogComponent.saveMessage(keyLocalStorage, message);
                    }
                });
            }
        } catch (e) {
            this.loading = false;
            this.addErrorMessage(e);
        }
    }

}
